import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  PublicEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';

import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import Modal from '@payaca/components/modal/Modal';

import { useSelector } from '@/api/state';
import { getUserRoles } from '@/utils/stateAccessors';
import EntityCard from '@payaca/components/entityCard/EntityCard';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import * as formActions from '@payaca/store/forms/formsActions';
import UntitledIcon from '@payaca/untitled-icons';
import { isNullish } from '@payaca/utilities/guards';
import { WithOptional } from '@payaca/utilities/types';
import { useHistory } from 'react-router';
import CreateEditEntityTemplateModal from '../createEditEntityTemplateModal/CreateEditEntityTemplateModal';
import './EntityTemplateCard.sass';

const readableEntityTypeMap: Record<TemplatableEntity, string> = {
  task: 'Task',
  scheduledEvent: 'Event',
  proposal: 'Proposal',
};

type Props = {
  allowedQuickActions?: ('edit' | 'delete')[];
  template: WithOptional<PublicEntityTemplate<TemplatableEntity>, 'publicId'>;
  onChange: (
    template: WithOptional<PublicEntityTemplate<TemplatableEntity>, 'publicId'>
  ) => Promise<void>;
  onRemove: () => Promise<void>;
  onClick?: (showEditTemplateModal: () => void) => void;
  allowModifyTemplateNameDescription?: boolean;
  showEntityTypeIcon?: boolean;
  editInModal?: boolean;
  isChildOfScheduledEvent?: boolean;
};

const EntityTemplateCard = ({
  template,
  onChange,
  onRemove,
  onClick,
  allowedQuickActions = ['edit', 'delete'],
  showEntityTypeIcon = true,
  allowModifyTemplateNameDescription,
  editInModal = true,
  isChildOfScheduledEvent,
}: Props) => {
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [isEditProcessing, setIsEditProcessing] = useState(false);
  const [editErrorMessage, setEditErrorMessage] = useState<string>();
  const templateIcon =
    template.entityType === 'scheduledEvent' ? (
      <UntitledIcon className="size-4" name="calendar-check-02.3" />
    ) : (
      <UntitledIcon className="size-4" name="list.3" />
    );
  const [showConfirmDeletePrompt, setShowConfirmDeletePrompt] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const userRoles = useSelector(getUserRoles);
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(formActions.requestGetAvailableForms());
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- dispatch is stable
  }, []);

  const availableForms = useSelector((state) => state.forms.availableForms);

  const quickActions = useMemo(() => {
    const qa = [];

    if (
      allowedQuickActions.includes('edit') &&
      userHasRequiredPermission(userRoles, [
        TemplatesPermissions.PERSIST_TEMPLATE,
      ])
    ) {
      qa.push({
        actionName: 'Edit',
        actionBehaviour: () => {
          if (!editInModal && template.publicId) {
            history.push(`/templates/edit/${template.publicId}`);
          } else {
            setShowEditTemplateModal(true);
          }
        },
      });
    }

    if (
      allowedQuickActions.includes('delete') &&
      userHasRequiredPermission(userRoles, [
        TemplatesPermissions.DELETE_TEMPLATE,
      ])
    ) {
      qa.push({
        actionName: 'Delete',
        actionBehaviour: () => {
          setShowConfirmDeletePrompt(true);
        },
        isActionProcessing: isRemoving,
      });
    }

    return qa;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'history'
  }, [
    isRemoving,
    allowedQuickActions,
    userRoles,
    editInModal,
    template.publicId,
  ]);

  const name = useMemo(() => {
    if (template.name?.length) {
      return (
        <>
          <strong>{template.name}</strong>
          {template?.data &&
            'name' in template.data &&
            !!template.data.name?.length && <span>{template?.data?.name}</span>}
        </>
      );
    } else if (
      template?.data &&
      'name' in template.data &&
      template.data.name?.length
    ) {
      return <span>{template.data.name}</span>;
    } else {
      return (
        <em>{`${readableEntityTypeMap[template.entityType]} template`}</em>
      );
    }
  }, [template]);

  const taskType = useMemo(() => {
    if (template.entityType !== 'task') {
      return;
    }

    const t = template as PublicEntityTemplate<'task'>;

    if (t.data.checklistItems?.length) {
      return <UntitledIcon className="task-type" name="check-square.3" />;
    } else if (!isNullish(t.data.formTemplateId)) {
      const form = availableForms.find(
        (f) => f.value.toString() === t.data.formTemplateId
      );
      if (!form || form.type === 'pdf') {
        return (
          <UntitledIcon
            className="task-type text-yellow-600"
            name="alert-triangle.3"
          />
        );
      }
      return <UntitledIcon className="task-type" name="file-06.3" />;
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'template'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: template.data
  }, [template.entityType, template.data, availableForms]);

  return (
    <>
      <EntityCard
        breakpointConfig={{
          downBreakpointXs: 400,
        }}
        className="entity-template-card"
        onClick={
          onClick
            ? () => onClick(() => setShowEditTemplateModal(true))
            : undefined
        }
        quickActionsConfig={{
          recordId: '',
          quickActions: quickActions,
        }}
      >
        {showEntityTypeIcon && templateIcon}
        <div className="name">{name}</div>
        {taskType}
      </EntityCard>

      <CreateEditEntityTemplateModal
        isOpen={showEditTemplateModal}
        onClose={() => {
          setShowEditTemplateModal(false);
          setEditErrorMessage(undefined);
        }}
        action="Edit"
        template={template}
        isChildOfScheduledEvent={isChildOfScheduledEvent}
        isProcessing={isEditProcessing}
        errorMessage={editErrorMessage}
        onRequestSave={(t) => {
          setIsEditProcessing(true);
          setEditErrorMessage(undefined);
          onChange(t)
            .then(() => {
              setIsEditProcessing(false);
              setShowEditTemplateModal(false);
            })
            .catch(() => {
              setIsEditProcessing(false);
              setEditErrorMessage('Failed to save template. Please try again.');
            });
        }}
        allowModifyTemplateNameDescription={allowModifyTemplateNameDescription}
      />
      <Modal
        isOpen={showConfirmDeletePrompt}
        onClose={() => setShowConfirmDeletePrompt(false)}
        title="Are you sure you want to delete this Template?"
        actions={
          <>
            <Button
              onClick={() => {
                setIsRemoving(true);
                // oxlint-disable-next-line typescript-eslint(no-floating-promises) -- FIXME: pre-existing error: Promises must be awaited.
                onRemove()
                  .then(() => {
                    setIsRemoving(false);
                  })
                  .finally(() => {
                    setShowConfirmDeletePrompt(false);
                  });
              }}
              styleVariant={ButtonStyleVariant.OUTSIZE}
            >
              Delete
            </Button>
            <Button
              styleVariant={ButtonStyleVariant.ANCHOR}
              onClick={() => setShowConfirmDeletePrompt(false)}
            >
              Cancel
            </Button>
          </>
        }
      ></Modal>
    </>
  );
};

export default EntityTemplateCard;
